import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Sparkles } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pulls reviews tagged with `tag` and shows up to 2 alongside the service.
 * Falls back gracefully when there are no tagged reviews.
 */
export default function RelatedReviews({ tag }) {
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews`, { params: { lang, tag } })
      .then((res) => { if (!cancelled) setReviews((res.data || []).slice(0, 2)); })
      .catch(() => { if (!cancelled) setReviews([]); });
    return () => { cancelled = true; };
  }, [lang, tag]);

  if (!reviews.length) return null;

  return (
    <section data-testid="related-reviews-section" className="bg-cream py-section border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="max-w-2xl">
          <span className="eyebrow"><Sparkles className="h-3 w-3 text-gold" />{t("service.related_reviews_eyebrow")}</span>
          <h2 className="mt-5 font-display text-h1 font-semibold text-ink">
            {t("service.related_reviews_title")}
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {reviews.map((r, i) => (
            <Reveal as="article" key={r.id} delay={i * 0.08}
              className="rounded-2xl border border-mist bg-white p-7 sm:p-8 flex flex-col"
              data-testid={`related-review-${i}`}
            >
              <div className="flex items-center gap-1 text-gold">
                {Array.from({ length: r.rating }).map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-5 font-display text-h3 leading-snug text-ink">“{r.text}”</blockquote>
              <div className="mt-auto pt-7 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">{r.author}</p>
                  <p className="text-xs text-ink/55">{r.date}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-medium uppercase tracking-[0.18em] text-ink/55 border border-mist rounded-full px-2.5 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  {r.source || "Google"}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
